/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { forwardRef, Ref, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { Selection, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { findFocusableElements } from 'utils/findFocusableElements';
import { Button } from 'components/_new/Button';
import { Box, BoxSection } from 'components/_new/Box';
import { useGlobal } from 'contexts/globalContext';
import { Overlay, OverlayElement, OverlayExtendProps } from 'components/_new/Overlay';
import { Icon } from 'components/_new/Icon';
import { insertEmbed } from '../../utils/embed';
import { EmbedEditForm, useEmbedEditForm } from './EmbedEditForm';
import { CustomEditor, EmbedBlockFormat } from '../../types';
import './Embed.scss';

type InsertEmbedPopupProps = {
	editor: CustomEditor;
	format: EmbedBlockFormat;
	uploadRequestFunction?: ((data: FormData) => Promise<string>) | undefined;
} & Pick<OverlayExtendProps, 'onOpenStateChange'>;

export const InsertEmbedPopup = forwardRef(
	({ onOpenStateChange, editor, format, uploadRequestFunction }: InsertEmbedPopupProps, ref: Ref<OverlayElement>) => {
		const { t } = useTranslation();
		const { toastRef } = useGlobal();

		const overlayRef = useRef<OverlayElement>(null);

		const [formState, formReset] = useEmbedEditForm();
		const [formData, setFormData] = formState;
		const { url } = formData;
		const [selection, setSelection] = useState<Selection>(null);
		const [isUploading, setIsUploading] = useState(false);
		const [afterUpload, setAfterUpload] = useState(false);

		const fileInputRef = useRef<HTMLInputElement>(null);

		useImperativeHandle(ref, () => ({
			open: (element: HTMLElement) => {
				//
			},
			close: () => {
				//
			},
			isOpen: () => {
				return false;
			},
			toggle: (element: HTMLElement) => {
				if (overlayRef?.current && element) {
					setSelection(editor.selection);
					if (selection) {
						ReactEditor.focus(editor);
					}

					overlayRef.current.toggle(element);
				}
			},
			getElement: () => overlayRef?.current?.getElement() || null,
		}));

		const handleClear = useCallback(() => {
			const overlayElement = overlayRef?.current?.getElement();
			if (overlayElement) {
				const focusable = findFocusableElements(overlayElement);
				const nextEl = focusable[1];
				if (nextEl) {
					nextEl.focus();
					setTimeout(() => {
						setFormData((prev) => ({ ...prev, url: '' }));
						setAfterUpload(false);
					}, 100);
				}
			}

			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const { mutateAsync: uploadImageMutateAsync } = uploadRequestFunction
			? useMutation(uploadRequestFunction)
			: { mutateAsync: () => new Promise<string>(() => {}) };

		// eslint-disable-next-line react-hooks/exhaustive-deps
		const uploadCallback = uploadRequestFunction
			? (file: File): Promise<object> => {
					const formData = new FormData();
					formData.append('file', file);

					const maxFileSize = 1024 * 1024 * 3; // 3 mb

					return new Promise((resolve, reject) => {
						if (file.size > maxFileSize) {
							toastRef?.current?.show({
								severity: 'error',
								life: 3000,
								summary: t(`misc.error`),
								detail: t('misc.fileSizeExceeds', { count: 3 }),
							});
							reject(new Error('max file size'));
						}
						uploadImageMutateAsync(formData)
							.then((link) => resolve({ data: { link } }))
							.catch((err) => {
								if (err instanceof AxiosError) {
									const message = err.response?.data.message || err.message;
									switch (message) {
										case 'File too large':
											toastRef?.current?.show({
												severity: 'error',
												life: 3000,
												summary: t(`misc.error`),
												detail: t('misc.fileSizeExceeds', { count: 3 }),
											});
											break;
										case 'Invalid image format':
											toastRef?.current?.show({
												severity: 'error',
												life: 3000,
												summary: t(`misc.error`),
												detail: t('misc.fileInvalidFileType'),
											});
											break;
										default:
									}
								} else {
									toastRef?.current?.show({
										severity: 'error',
										life: 3000,
										summary: t(`misc.error`),
										detail: t('misc.fileUnexpectedError'),
									});
								}
								reject(new Error('unexpected upload error'));
							});
					});
			  }
			: undefined;

		const handleFileUpload = useCallback(
			async (file: File) => {
				if (!uploadCallback) return;

				setIsUploading(true);
				try {
					const result = await uploadCallback(file);
					const url = (result as { data: { link: string } }).data.link;
					setFormData((prev) => ({ ...prev, url }));
					setAfterUpload(true);
				} catch (error) {
					console.error('Upload error:', error);
				} finally {
					setIsUploading(false);
				}
			},
			[uploadCallback, setFormData]
		);

		const handleFileInputChange = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				const file = event.target.files?.[0];
				if (file) {
					handleFileUpload(file);
				}
				if (event.target) {
					// eslint-disable-next-line no-param-reassign
					event.target.value = '';
				}
			},
			[handleFileUpload]
		);

		const handleUploadButtonClick = useCallback(() => {
			fileInputRef.current?.click();
		}, []);

		const onDrop = useCallback(
			(acceptedFiles: File[]) => {
				if (acceptedFiles.length > 0) {
					handleFileUpload(acceptedFiles[0]);
				}
			},
			[handleFileUpload]
		);

		const { getRootProps, getInputProps, isDragActive } = useDropzone({
			onDrop,
			accept: {
				'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
			},
			maxFiles: 1,
			disabled: isUploading || !uploadCallback,
			noClick: true,
			noKeyboard: true,
		});

		const handleFormSubmit = () => {
			if (selection) {
				Transforms.select(editor, selection);
				ReactEditor.focus(editor);
			}

			insertEmbed(editor, formData, format);
			formReset();

			overlayRef?.current?.close();
		};

		return (
			<Overlay ref={overlayRef} onOpenStateChange={onOpenStateChange}>
				<Box variant="white-bordered" className="editor-popup" slim dropShadow>
					<BoxSection>
						{format === 'image' && uploadCallback && (
							// eslint-disable-next-line react/jsx-no-useless-fragment
							<>
								{!afterUpload ? (
									<>
										<input
											ref={fileInputRef}
											type="file"
											accept="image/*"
											onChange={handleFileInputChange}
											style={{ display: 'none' }}
										/>

										<div
											{...getRootProps()}
											className={`border-2 border-dashed border-round p-4 text-center cursor-pointer transition-colors transition-duration-200 ${
												isDragActive
													? 'border-primary bg-primary-50'
													: 'border-gray-300 hover:border-primary hover:bg-gray-50'
											}`}
											style={{
												minHeight: '120px',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'center',
												gap: '0.5rem',
											}}
										>
											<input {...getInputProps()} />
											{isUploading ? (
												<>
													<Icon
														name="circle-notch"
														iconSet="fa"
														iconStyle="solid"
														className="fa-spin text-primary"
														style={{ fontSize: '2rem' }}
													/>
													<span className="text-color-secondary">Przesyłanie...</span>
												</>
											) : (
												<>
													<Icon
														name="cloud-upload-alt"
														iconSet="fa"
														iconStyle="solid"
														className="text-gray-400"
														style={{ fontSize: '2rem' }}
													/>
													<span className="text-color-secondary">
														{isDragActive
															? 'Upuść plik tutaj'
															: 'Przeciągnij i upuść obraz tutaj'}
													</span>
													<span className="text-color-secondary text-sm">lub</span>
													<Button
														label="Prześlij"
														variant="primary"
														onClick={handleUploadButtonClick}
														disabled={isUploading}
														loading={isUploading}
													/>
													<span className="text-color-secondary text-xs mt-2">
														Maksymalny rozmiar pliku: 3 MB
													</span>
												</>
											)}
										</div>
										<span className="w-full text-center text-gray my-2">LUB</span>
									</>
								) : (
									<div className="flex flex-row justify-content-center">
										<Button
											label="Wyczyść"
											onClick={handleClear}
											variant="danger"
											variantSize="sm"
											icon="trash"
										/>
									</div>
								)}
							</>
						)}

						<EmbedEditForm formState={formState} />

						<div className="flex flex-row justify-content-end mt-3">
							<Button label="Zapisz" onClick={handleFormSubmit} disabled={!formData.url} />
						</div>
					</BoxSection>
				</Box>
			</Overlay>
		);
	}
);
